import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { usePartnerCode } from "@/hooks/usePartnerCode";

interface FormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  referralCode: string;
  useReferralCode: boolean;
  heardAboutSource: string;
}

interface SignUpFormInputsProps {
  formData: FormData;
  setFormData: (data: FormData) => void;
}

const HEARD_ABOUT_OPTIONS = [
  "Instagram",
  "TikTok",
  "Friend or family",
  "Google search",
  "Event or partner",
  "Press or article",
  "Other",
];

const SignUpFormInputs = ({
  formData,
  setFormData,
}: SignUpFormInputsProps) => {
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { partnerCode, codeInfo } = usePartnerCode();

  useEffect(() => {
    if (formData.password && formData.password.length < 6) {
      setPasswordError("Password must be at least 6 characters long");
    } else {
      setPasswordError(null);
    }
  }, [formData.password]);

  const handleChange =
    (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: e.target.value });
    };

  return (
    <div className="space-y-4">
      {partnerCode && codeInfo && (
        <Alert className="bg-secondary/10 border-secondary/20 border-2">
          <AlertDescription className="space-y-1">
            <div className="text-lg font-semibold text-secondary">Partner Offer Activated! 🎉</div>
            <div className="text-primary-foreground">
              You're being referred by <span className="font-semibold">{codeInfo.partner_name}</span>
            </div>
            <div className="text-sm text-primary-foreground/90">
              You'll receive {codeInfo.credits_value} credits (worth £{codeInfo.credits_value * 2.5}) after signup
            </div>
          </AlertDescription>
        </Alert>
      )}
      
      <Input
        type="text"
        placeholder="First Name"
        value={formData.firstName}
        onChange={handleChange("firstName")}
        required
        className="bg-primary border-secondary text-primary-foreground placeholder:text-muted-foreground"
      />
      <Input
        type="text"
        placeholder="Last Name"
        value={formData.lastName}
        onChange={handleChange("lastName")}
        required
        className="bg-primary border-secondary text-primary-foreground placeholder:text-muted-foreground"
      />

      <Input
        type="tel"
        placeholder="Phone Number"
        value={formData.phone}
        onChange={handleChange("phone")}
        required
        className="bg-primary border-secondary text-primary-foreground placeholder:text-muted-foreground"
      />

      <Input
        type="email"
        placeholder="Email address"
        value={formData.email}
        onChange={handleChange("email")}
        required
        className="bg-primary border-secondary text-primary-foreground placeholder:text-muted-foreground"
      />

      <div className="space-y-2">
        <Input
          type="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange("password")}
          required
          className={`bg-primary border-secondary text-primary-foreground placeholder:text-muted-foreground ${
            passwordError ? "border-red-500" : ""
          }`}
        />
        {passwordError && (
          <Alert variant="destructive">
            <AlertDescription>{passwordError}</AlertDescription>
          </Alert>
        )}
      </div>

      <Select
        value={formData.heardAboutSource || undefined}
        onValueChange={(value) =>
          setFormData({ ...formData, heardAboutSource: value })
        }
      >
        <SelectTrigger className="bg-primary border-secondary text-primary-foreground">
          <SelectValue placeholder="How did you hear about us?" />
        </SelectTrigger>
        <SelectContent>
          {HEARD_ABOUT_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!partnerCode && (
        <>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="useReferralCode"
              checked={formData.useReferralCode}
              onChange={() =>
                setFormData({
                  ...formData,
                  useReferralCode: !formData.useReferralCode,
                })
              }
              className="h-4 w-4 text-secondary border-secondary
                focus:ring-2 focus:ring-secondary"
            />
            <label
              htmlFor="useReferralCode"
              className="text-primary-foreground cursor-pointer"
            >
              I have a referral code&nbsp;
              <span className="text-sm text-muted-foreground">(this is not required)</span>
            </label>
          </div>

          {formData.useReferralCode && (
            <Input
              type="text"
              placeholder="Referral Code"
              value={formData.referralCode}
              onChange={handleChange("referralCode")}
              className="bg-primary border-secondary text-primary-foreground placeholder:text-muted-foreground"
            />
          )}
        </>
      )}
    </div>
  );
};

export default SignUpFormInputs;
